"use client";

import { useEffect, useRef, useState, useCallback } from "react";

type WSStatus = "connecting" | "connected" | "disconnected" | "error";

interface UseWebSocketOptions {
  onMessage?: (data: unknown) => void;
  onAlert?: (alert: WSAlert) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

interface WSAlert {
  key: string;
  severity: "critical" | "warning" | "info";
  message: string;
  time: string;
  type: "alert";
}

interface WSOrderUpdate {
  type: "order_update";
  order: {
    key: string;
    id: string;
    symbol: string;
    status: string;
    filled: number;
    quantity: number;
    price: number;
  };
}

interface WSDashboardUpdate {
  type: "dashboard_update";
  metrics: Record<string, unknown>;
}

type WSMessage = WSAlert | WSOrderUpdate | WSDashboardUpdate | { type: string; [key: string]: unknown };

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const {
    onMessage,
    onAlert,
    reconnectInterval = 3000,
    maxReconnectAttempts = 10,
  } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const [status, setStatus] = useState<WSStatus>("disconnected");
  const [alerts, setAlerts] = useState<WSAlert[]>([]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Prefer env. Only use prod brand WS on known prod hosts; unknown/DEV → api.sandbox
    // (never default to ws.mechagora.net on platform-dev / internal / localhost).
    const envWs = process.env.NEXT_PUBLIC_WS_BASE_URL?.trim();
    const host =
      typeof window !== "undefined" ? window.location.hostname : "";
    const useProdWs =
      host.includes("employee.mechagora.com") ||
      host.includes("employee.edge.") ||
      host === "employee.mechagora.internal";
    const wsBase =
      envWs ||
      (useProdWs ? "wss://ws.mechagora.net" : "wss://api.sandbox.mechagora.net");
    const sessionCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("employee_session="))
      ?.split("=")[1];

    if (!sessionCookie) {
      // Not authenticated yet — retry later
      reconnectTimerRef.current = setTimeout(connect, 5000);
      return;
    }

    const url = new URL(wsBase);
    url.searchParams.set("token", sessionCookie);

    setStatus("connecting");
    const ws = new WebSocket(url.toString());
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      setStatus("connected");
      reconnectCountRef.current = 0;
      // Subscribe to channels
      ws.send(JSON.stringify({ type: "subscribe", channels: ["orders", "alerts", "dashboard"] }));
      // Start heartbeat
      const heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
      ws.addEventListener("close", () => clearInterval(heartbeat));
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const msg = JSON.parse(event.data) as WSMessage;

        if (msg.type === "pong") return;

        if (msg.type === "alert") {
          const alert = msg as WSAlert;
          setAlerts((prev) => [alert, ...prev].slice(0, 50));
          onAlert?.(alert);
        }

        onMessage?.(msg);
      } catch {
        // Ignore non-JSON messages
      }
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      setStatus("disconnected");
      wsRef.current = null;
      attemptReconnect();
    };

    ws.onerror = () => {
      setStatus("error");
      ws.close();
    };
  }, [onMessage, onAlert]);

  const attemptReconnect = useCallback(() => {
    if (reconnectCountRef.current >= maxReconnectAttempts) {
      setStatus("error");
      return;
    }
    const delay = Math.min(
      reconnectInterval * Math.pow(1.5, reconnectCountRef.current),
      30000
    );
    reconnectCountRef.current++;
    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) connect();
    }, delay);
  }, [connect, reconnectInterval, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectCountRef.current = maxReconnectAttempts; // prevent reconnect
    wsRef.current?.close();
    wsRef.current = null;
    setStatus("disconnected");
  }, [maxReconnectAttempts]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((data: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const dismissAlert = useCallback((key: string) => {
    setAlerts((prev) => prev.filter((a) => a.key !== key));
  }, []);

  return { status, alerts, send, disconnect, dismissAlert };
}
